'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Film,
  Loader2,
  Pencil,
  Play,
  RefreshCw,
  Square,
  Trash2,
  Volume2,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { useGuidedTour } from './GuidedTourProvider';
import {
  fetchTourMediaJobs,
  fetchTourMediaStatus,
  isTourMediaRegenAvailable,
  requestTourMediaRegen,
  setTourFlowVoice,
  tourMediaJobAction,
  TOUR_MEDIA_MANAGER_EVENT,
  type TourAudioStatusEntry,
  type TourMediaFlowStatus,
  type TourMediaJobInfo,
  type TourMediaManagerEventDetail,
  type TourMediaStatus,
} from './utils/tourMediaRegen';
import { WideSlideOver, SlideOverHeaderAction } from '@/components/SlideOver';
import { KOKORO_VOICES } from '@/lib/ai/ttsVoiceData';
import { cn } from '@/lib/utils';

/**
 * GUIDED TOUR MEDIA MANAGER — dev + root only
 *
 * Slide-over panel that puts the whole tour system's generated media in one
 * place: every flow, every step, whether its coach-audio WAV exists, how old
 * it is, and how long it runs — plus per-step / per-flow / module-wide
 * regeneration buttons backed by the BullMQ general queue with live progress.
 *
 * This surface replaces the old scheduled media backfills: nothing touches
 * WAVs or MP4s unless a root user clicks here. Regeneration writes through
 * the same hash-checked manifest pipeline the serve path validates against.
 *
 * Open by dispatching:
 *   window.dispatchEvent(new CustomEvent(TOUR_MEDIA_MANAGER_EVENT, {
 *     detail: { moduleKey, flowId? }
 *   }))
 */

const ACTIVE_JOB_STATES = new Set(['waiting', 'active', 'delayed', 'prioritized', 'waiting-children']);
const POLL_MS = 3500;

function formatDuration(seconds?: number): string | null {
  if (!seconds || seconds <= 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatRelative(iso?: string): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const AUDIO_STATUS_LABEL: Record<string, string> = {
  ready: 'Ready',
  stale: 'Stale',
  missing: 'Missing',
  none: 'No script',
};

const AUDIO_STATUS_CLS: Record<string, string> = {
  ready: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  stale: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  missing: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  none: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

export function GuidedTourMediaManager() {
  const { moduleKey, openTour } = useGuidedTour();
  const available = isTourMediaRegenAvailable();

  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<TourMediaStatus | null>(null);
  const [jobs, setJobs] = useState<TourMediaJobInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);
  const [actingJobId, setActingJobId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hadActiveRef = useRef(false);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────

  const loadStatus = useCallback(async () => {
    if (!moduleKey) return;
    setLoading(true);
    try {
      const next = await fetchTourMediaStatus(moduleKey);
      if (next) {
        setStatus(next);
        setJobs(next.jobs);
        setExpanded((prev) => {
          if (prev.size > 0) return prev;
          // First load: expand flows that have any non-ready media so the
          // author sees problems without hunting.
          const initial = new Set<string>();
          for (const f of next.flows) {
            const hasIssue =
              f.video.status !== 'ready' ||
              f.intro.status === 'stale' ||
              f.intro.status === 'missing' ||
              f.steps.some(
                (s) =>
                  s.narration.status === 'stale' ||
                  s.narration.status === 'missing' ||
                  s.titleAudio.status === 'stale' ||
                  s.titleAudio.status === 'missing',
              );
            // Accordions are exclusive — only the first flow with an issue
            // opens; otherwise the first flow.
            if (hasIssue) {
              initial.add(f.id);
              break;
            }
          }
          return initial.size > 0
            ? initial
            : new Set(next.flows.length ? [next.flows[0].id] : []);
        });
      } else {
        setStatus(null);
      }
    } finally {
      setLoading(false);
    }
  }, [moduleKey]);

  const pollJobs = useCallback(async () => {
    if (!moduleKey) return;
    // This invocation consumed the pending timer (if any) — clear the stale
    // handle so the scheduler effect can arm the next tick.
    pollTimerRef.current = null;
    const next = await fetchTourMediaJobs(moduleKey);
    setJobs(next);
    const hasActive = next.some((j) => ACTIVE_JOB_STATES.has(j.state));
    if (hadActiveRef.current && !hasActive) {
      // A run just finished — pull the full inventory so statuses/durations
      // reflect the files the worker just wrote.
      hadActiveRef.current = false;
      void loadStatus();
      const failed = next.find((j) => j.state === 'failed');
      const done = next.find((j) => j.state === 'completed');
      if (failed) {
        toast.error(`Media job failed: ${failed.failedReason ?? 'unknown error'}`);
      } else if (done?.result) {
        const r = done.result;
        const made = (r.generated ?? r.rendered ?? 0) as number;
        toast.success(
          `Media generation finished — ${made} file${made === 1 ? '' : 's'} produced` +
            (r.failed ? `, ${r.failed} failed` : ''),
        );
      }
    }
    hadActiveRef.current = hasActive;
    if (hasActive) {
      pollTimerRef.current = setTimeout(() => void pollJobs(), POLL_MS);
    }
  }, [moduleKey, loadStatus]);

  // Queue controls — waiting jobs can be pulled; active jobs get a cancel flag
  // the worker honors at the next item boundary; finished jobs retry/delete.
  const jobAction = useCallback(
    async (job: TourMediaJobInfo, action: 'cancel' | 'retry' | 'delete') => {
      setActingJobId(job.id);
      try {
        const r = await tourMediaJobAction(job.id, action);
        if (!r.success) {
          toast.error(r.error ?? `Failed to ${action} job`);
        }
        await pollJobs();
      } finally {
        setActingJobId(null);
      }
    },
    [pollJobs],
  );

  // Open/close via window event (module-scoped so only the matching provider's
  // mounted manager responds).
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<TourMediaManagerEventDetail>).detail;
      if (detail?.moduleKey && detail.moduleKey !== moduleKey) return;
      setIsOpen(true);
      if (detail?.flowId) {
        setExpanded(new Set([detail.flowId]));
      }
    };
    window.addEventListener(TOUR_MEDIA_MANAGER_EVENT, handler);
    return () => window.removeEventListener(TOUR_MEDIA_MANAGER_EVENT, handler);
  }, [moduleKey]);

  // Load on open; poll while jobs are in flight.
  useEffect(() => {
    if (!isOpen) return;
    void loadStatus();
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isOpen, loadStatus]);

  useEffect(() => {
    if (!isOpen) return;
    const hasActive = jobs.some((j) => ACTIVE_JOB_STATES.has(j.state));
    if (hasActive && !pollTimerRef.current) {
      hadActiveRef.current = true;
      pollTimerRef.current = setTimeout(() => void pollJobs(), POLL_MS);
    }
    if (!hasActive && pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, isOpen]);

  // Stop preview audio when the modal closes.
  useEffect(() => {
    if (!isOpen) {
      audioRef.current?.pause();
      setPlayingUrl(null);
    }
  }, [isOpen]);

  // ── Actions ───────────────────────────────────────────────────────────

  const regen = useCallback(
    async (opts: {
      flowId?: string;
      stepIds?: string[];
      kind: 'audio' | 'video' | 'all';
      force?: boolean;
      confirmText?: string;
    }) => {
      const key = `${opts.kind}:${opts.flowId ?? '*'}:${(opts.stepIds ?? []).join(',')}:${
        opts.force === false ? 'fill' : 'force'
      }`;
      if (busyKeys.has(key)) return;
      if (
        opts.confirmText &&
        !window.confirm(opts.confirmText)
      ) {
        return;
      }
      setBusyKeys((prev) => new Set(prev).add(key));
      try {
        const res = await requestTourMediaRegen({
          moduleKey,
          flowId: opts.flowId,
          stepIds: opts.stepIds,
          kind: opts.kind,
          force: opts.force,
        });
        if (res.success) {
          toast.success(res.message ?? 'Queued');
          void pollJobs();
        } else {
          toast.error(res.error ?? 'Regeneration failed to queue');
        }
      } finally {
        setBusyKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [busyKeys, moduleKey, pollJobs],
  );

  // Persist a flow's narration voice to the module YAML. Changing the voice
  // re-keys every manifest lookup for the flow, so affected steps flip to
  // "missing" — the user then regenerates (force or fill) to record them.
  const setFlowVoice = useCallback(
    async (flowId: string, voice: string) => {
      const key = `voice:${flowId}`;
      if (busyKeys.has(key) || !moduleKey) return;
      setBusyKeys((prev) => new Set(prev).add(key));
      try {
        const res = await setTourFlowVoice({ moduleKey, flowId, voice });
        if (res.success) {
          const label = KOKORO_VOICES.find((v) => v.id === voice)?.name ?? voice;
          toast.success(`Voice set to ${label} — regenerate the flow's audio to apply`);
          void loadStatus();
        } else {
          toast.error(res.error ?? 'Failed to save voice');
        }
      } finally {
        setBusyKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [busyKeys, moduleKey, loadStatus],
  );

  const playAudio = useCallback((url?: string) => {
    if (!url) return;
    if (playingUrl === url) {
      audioRef.current?.pause();
      setPlayingUrl(null);
      return;
    }
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.onended = () => setPlayingUrl(null);
    }
    audioRef.current.src = url;
    setPlayingUrl(url);
    void audioRef.current.play().catch(() => setPlayingUrl(null));
  }, [playingUrl]);

  const editStep = useCallback(
    (flowId: string, stepId?: string) => {
      try {
        sessionStorage.setItem('guided-tour:edit-intent', stepId ? 'step' : 'flow');
      } catch { /* sessionStorage unavailable */ }
      setIsOpen(false);
      openTour({ flowId, stepId, format: 'interactive' });
    },
    [openTour],
  );

  // Exclusive accordion — opening a flow closes whichever was open.
  const toggleFlow = useCallback((flowId: string) => {
    setExpanded((prev) => (prev.has(flowId) ? new Set<string>() : new Set([flowId])));
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────

  const activeJobs = useMemo(() => jobs.filter((j) => ACTIVE_JOB_STATES.has(j.state)), [jobs]);
  const recentJobs = useMemo(
    () => jobs.filter((j) => !ACTIVE_JOB_STATES.has(j.state)).slice(0, 5),
    [jobs],
  );

  if (!available || !isOpen) return null;

  const stats = status?.stats;

  return (
    <WideSlideOver
      onClose={() => setIsOpen(false)}
      icon={Volume2}
      title={`Tour media — ${status?.moduleName ?? moduleKey}`}
      subtitle="Generated audio/video for every tour step. Regeneration only happens from here — nothing is scheduled."
      headerActions={
        <SlideOverHeaderAction
          icon={RefreshCw}
          title="Refresh"
          onClick={() => void loadStatus()}
          disabled={loading}
        />
      }
      zIndex={120}
      loading={loading && !status}
      hasContent={!!status}
      emptyState={
        <div className="flex h-64 flex-col items-center justify-center gap-3 text-slate-400">
          <Volume2 className="h-8 w-8" />
          <p className="text-sm">Could not load media status for this module.</p>
        </div>
      }
      contentClassName="flex-1 overflow-y-auto bg-slate-900"
    >
      {status && (
        <div className="space-y-4 px-5 py-4">
          {/* Warnings */}
          {!status.audioDirAvailable && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-2.5 text-xs text-rose-200">
              Coach-audio directory is not mounted — WAV files can't be
              written or verified until the NAS mount is back.
            </div>
          )}

          {/* Stats + module actions */}
          <div className="flex flex-wrap items-center gap-2">
            <StatChip label="Flows" value={stats?.flows ?? 0} />
            <StatChip label="Steps" value={stats?.steps ?? 0} />
            <StatChip
              label="Audio ready"
              value={stats?.audio.ready ?? 0}
              tone="emerald"
            />
            <StatChip
              label="Stale"
              value={stats?.audio.stale ?? 0}
              tone="amber"
            />
            <StatChip
              label="Missing"
              value={stats?.audio.missing ?? 0}
              tone="rose"
            />
            <StatChip
              label="Videos"
              value={stats?.videos.ready ?? 0}
              tone="indigo"
            />
            {(stats?.missingTargets ?? 0) > 0 && (
              <span
                className="inline-flex items-baseline gap-1.5 rounded-lg border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-xs"
                title="Steps whose spotlight/verification target (data-tour-id) doesn't exist in the codebase — the runner collapses to 'This section is not visible' on these."
              >
                <span className="font-semibold text-rose-300">{stats!.missingTargets}</span>
                <span className="text-rose-300/80">Broken targets</span>
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                disabled={busyKeys.has('audio:*:*:fill') || busyKeys.has('audio:*:*:force')}
                onClick={() =>
                  void regen({ kind: 'audio', force: false })
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/15 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/25 disabled:opacity-50"
              >
                {busyKeys.has('audio:*:') ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Volume2 className="h-3.5 w-3.5" />
                )}
                Fill missing &amp; stale
              </button>
              <button
                type="button"
                disabled={busyKeys.has('audio:*:*:force') || busyKeys.has('audio:*:*:fill')}
                onClick={() =>
                  void regen({
                    kind: 'audio',
                    force: true,
                    confirmText:
                      `Re-synthesize ALL coach audio for ${status.moduleName}? ` +
                      `Every WAV in this module gets replaced (working ones included).`,
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Regenerate all audio
              </button>
              <button
                type="button"
                disabled={busyKeys.has('video:*:*:fill') || busyKeys.has('video:*:*:force')}
                onClick={() =>
                  void regen({ kind: 'video', force: false })
                }
                title="Record walkthrough videos only for flows whose video is missing or stale"
                className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-500/40 bg-indigo-500/15 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/25 disabled:opacity-50"
              >
                {busyKeys.has('video:*:*:fill') ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Film className="h-3.5 w-3.5" />
                )}
                Record missing &amp; stale
              </button>
              <button
                type="button"
                disabled={busyKeys.has('video:*:*:force') || busyKeys.has('video:*:*:fill')}
                onClick={() =>
                  void regen({
                    kind: 'video',
                    force: true,
                    confirmText:
                      `Re-record ALL walkthrough videos for ${status.moduleName}? ` +
                      `Every MP4 in this module gets replaced (working ones included) — each recording is a live Playwright run.`,
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/15 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-500/25 disabled:opacity-50"
              >
                <Film className="h-3.5 w-3.5" />
                Re-record all videos
              </button>
            </div>
          </div>

          {/* Active jobs */}
          {activeJobs.length > 0 && (
            <div className="space-y-2">
              {activeJobs.map((job) => {
                const progress = typeof job.progress === 'object' && job.progress
                  ? job.progress
                  : null;
                const pct =
                  progress?.done != null && progress?.total
                    ? Math.round((progress.done / progress.total) * 100)
                    : null;
                return (
                  <div
                    key={job.id}
                    className="rounded-lg border border-indigo-500/30 bg-indigo-500/10 px-4 py-2.5"
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-indigo-200">
                        {job.name === 'coach-audio-backfill' ? 'Coach audio' : 'Tour video'}
                        {job.flowId ? ` · ${job.flowId}` : ' · all flows'}
                        {job.state === 'waiting' ? ' — queued' : job.state === 'active' ? ' — running' : ` — ${job.state}`}
                      </span>
                      <span className="flex items-center gap-1.5">
                        {pct != null && (
                          <span className="text-indigo-300">
                            {progress?.done}/{progress?.total} ({pct}%)
                          </span>
                        )}
                        <button
                          type="button"
                          title={job.state === 'active' ? 'Stop at next item' : 'Remove from queue'}
                          disabled={actingJobId === job.id}
                          onClick={() => void jobAction(job, job.state === 'active' ? 'cancel' : 'delete')}
                          className="flex items-center gap-1 rounded border border-rose-500/40 px-1.5 py-0.5 text-[10px] font-medium text-rose-300 hover:bg-rose-500/10 disabled:opacity-50"
                        >
                          {actingJobId === job.id
                            ? <Loader2 className="h-2.5 w-2.5 animate-spin" />
                            : <Square className="h-2.5 w-2.5" />}
                          {job.state === 'active' ? 'Stop' : 'Remove'}
                        </button>
                      </span>
                    </div>
                    {pct != null && (
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                        <div
                          className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Expand/collapse all */}
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Flows &amp; steps
            </p>
            {expanded.size > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(new Set())}
                className="text-xs text-indigo-300 hover:text-indigo-200"
              >
                Collapse
              </button>
            )}
          </div>

          {/* Flow list */}
          <div className="space-y-2">
            {status.flows.map((flow) => (
              <FlowSection
                key={`${flow.sourceFile}/${flow.id}`}
                flow={flow}
                expanded={expanded.has(flow.id)}
                onToggle={() => toggleFlow(flow.id)}
                busyKeys={busyKeys}
                onRegen={regen}
                onPlay={playAudio}
                playingUrl={playingUrl}
                onEditStep={editStep}
                voice={flow.intro.voice || 'af_heart'}
                voiceBusy={busyKeys.has(`voice:${flow.id}`)}
                onVoiceChange={(v) => void setFlowVoice(flow.id, v)}
              />
            ))}
          </div>

          {/* Recent finished jobs */}
          {recentJobs.length > 0 && (
            <div className="border-t border-slate-800 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Recent runs
              </p>
              <div className="space-y-1">
                {recentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between rounded px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-800/60"
                  >
                    <span>
                      {job.name === 'coach-audio-backfill' ? 'Audio' : 'Video'}
                      {job.flowId ? ` · ${job.flowId}` : ''}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {(job.state === 'failed' || job.state === 'completed') && (
                        <button
                          type="button"
                          title="Re-queue this run"
                          disabled={actingJobId === job.id}
                          onClick={() => void jobAction(job, 'retry')}
                          className="rounded border border-slate-600 px-1.5 py-0.5 text-[10px] font-medium text-slate-300 hover:border-indigo-400 hover:text-indigo-300 disabled:opacity-50"
                        >
                          {actingJobId === job.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : 'Retry'}
                        </button>
                      )}
                      <button
                        type="button"
                        title="Delete record"
                        disabled={actingJobId === job.id}
                        onClick={() => void jobAction(job, 'delete')}
                        className="rounded border border-slate-700 px-1 py-0.5 text-slate-500 hover:border-rose-500/40 hover:text-rose-300 disabled:opacity-50"
                      >
                        {actingJobId === job.id ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Trash2 className="h-2.5 w-2.5" />}
                      </button>
                      <span
                        className={cn(
                          job.state === 'completed' && 'text-emerald-400',
                          job.state === 'failed' && 'text-rose-400',
                        )}
                      >
                        {job.state === 'completed' && job.result
                          ? `${job.result.generated ?? job.result.rendered ?? 0} made${
                              job.result.failed ? `, ${job.result.failed} failed` : ''
                            }`
                          : job.failedReason ?? job.state}
                        {job.finishedOn ? ` · ${formatRelative(new Date(job.finishedOn).toISOString())}` : ''}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </WideSlideOver>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function StatChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'emerald' | 'amber' | 'rose' | 'indigo';
}) {
  const toneCls =
    tone === 'emerald'
      ? 'text-emerald-300'
      : tone === 'amber'
        ? 'text-amber-300'
        : tone === 'rose'
          ? 'text-rose-300'
          : tone === 'indigo'
            ? 'text-indigo-300'
            : 'text-slate-200';
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-slate-700/60 bg-slate-800/60 px-2.5 py-1 text-xs">
      <span className={cn('font-semibold', toneCls)}>{value}</span>
      <span className="text-slate-500">{label}</span>
    </span>
  );
}

function AudioStatusBadge({ entry }: { entry: TourAudioStatusEntry }) {
  const duration = formatDuration(entry.durationSeconds);
  const label =
    entry.status === 'stale'
      ? entry.staleReason === 'old-gen'
        ? 'Old gen'
        : 'Stale'
      : AUDIO_STATUS_LABEL[entry.status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium',
        AUDIO_STATUS_CLS[entry.status],
      )}
      title={
        entry.status === 'stale' && entry.staleReason === 'text-changed'
          ? 'Narration text changed since this audio was generated — it is not being served.'
          : entry.status === 'stale'
            ? 'Generated by an older synthesis version — still serves, but queued for refresh.'
            : undefined
      }
    >
      {label}
      {duration && <span className="font-normal opacity-75">{duration}</span>}
    </span>
  );
}

function FlowSection({
  flow,
  expanded,
  onToggle,
  busyKeys,
  onRegen,
  onPlay,
  playingUrl,
  onEditStep,
  voice,
  voiceBusy,
  onVoiceChange,
}: {
  flow: TourMediaFlowStatus;
  expanded: boolean;
  onToggle: () => void;
  busyKeys: Set<string>;
  onRegen: (opts: {
    flowId?: string;
    stepIds?: string[];
    kind: 'audio' | 'video' | 'all';
    force?: boolean;
    confirmText?: string;
  }) => Promise<void>;
  onPlay: (url?: string) => void;
  playingUrl: string | null;
  onEditStep: (flowId: string, stepId?: string) => void;
  voice: string;
  voiceBusy: boolean;
  onVoiceChange: (voice: string) => void;
}) {
  const audioCounts = useMemo(() => {
    let ready = 0;
    let total = 0;
    for (const s of flow.steps) {
      for (const e of [s.narration, s.titleAudio]) {
        if (e.status !== 'none') total++;
        if (e.status === 'ready') ready++;
      }
    }
    if (flow.intro.status !== 'none') {
      total++;
      if (flow.intro.status === 'ready') ready++;
    }
    const missingTargets = flow.steps.reduce(
      (n, s) => n + (s.targets ?? []).filter((t) => t.status === 'missing').length,
      0,
    );
    return { ready, total, missingTargets };
  }, [flow]);

  const audioBusy =
    busyKeys.has(`audio:${flow.id}::force`) ||
    busyKeys.has('audio:*:*:force') ||
    busyKeys.has('audio:*:*:fill');
  const videoBusy = busyKeys.has(`video:${flow.id}::force`);
  const videoDuration = formatDuration(flow.video.durationSeconds);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-700/70 bg-slate-800/40">
      {/* Flow header */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
          )}
          <span className="truncate text-sm font-medium text-white">{flow.label}</span>
          <span className="shrink-0 text-[10px] text-slate-500">
            {flow.steps.length} step{flow.steps.length === 1 ? '' : 's'}
          </span>
          {flow.sourceFile !== 'desktop' && (
            <span className="shrink-0 rounded bg-slate-700/80 px-1.5 py-0.5 text-[10px] text-slate-300">
              {flow.sourceFile}
            </span>
          )}
        </button>

        <span className="shrink-0 text-[10px] text-slate-400">
          audio {audioCounts.ready}/{audioCounts.total}
        </span>
        {audioCounts.missingTargets > 0 && (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-amber-300"
            title={`${audioCounts.missingTargets} spotlight/verification target(s) in this flow don't resolve to a data-tour-id in the codebase`}
          >
            <AlertTriangle className="h-3 w-3" />
            {audioCounts.missingTargets}
          </span>
        )}
        <VideoBadge video={flow.video} duration={videoDuration} />

        <select
          value={voice}
          disabled={voiceBusy}
          onChange={(e) => onVoiceChange(e.target.value)}
          title="Narration voice for this flow — saved to the module YAML. Steps with their own tts_voice keep it. Regenerate the flow's audio to apply."
          className="shrink-0 rounded-md border border-slate-600 bg-slate-900 px-1.5 py-0.5 text-[10px] text-slate-300 focus:border-indigo-500 focus:outline-none disabled:opacity-50"
        >
          {KOKORO_VOICES.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name} · {v.accent}
            </option>
          ))}
          {/* Voice set in the YAML but absent from the static catalog */}
          {voice && !KOKORO_VOICES.some((v) => v.id === voice) && (
            <option value={voice}>{voice}</option>
          )}
        </select>

        <div className="flex shrink-0 items-center gap-1">
          <IconBtn
            title="Regenerate this flow's audio"
            busy={audioBusy}
            onClick={() => void onRegen({ flowId: flow.id, kind: 'audio', force: true })}
          >
            <Volume2 className="h-3.5 w-3.5" />
          </IconBtn>
          <IconBtn
            title="Re-record this flow's walkthrough video"
            busy={videoBusy}
            onClick={() => void onRegen({ flowId: flow.id, kind: 'video', force: true })}
          >
            <Film className="h-3.5 w-3.5" />
          </IconBtn>
          <IconBtn
            title="Edit tour intro text"
            onClick={() => onEditStep(flow.id)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </IconBtn>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-slate-700/60">
          {/* Intro (__summary__) row */}
          <div className="flex items-center gap-2 border-b border-slate-700/40 px-3 py-2 text-xs">
            <span className="w-6 shrink-0 text-center text-[10px] font-semibold text-indigo-400">i</span>
            <span className="min-w-0 flex-1 truncate text-slate-300">
              Welcome narration{flow.intro.voice ? ` · ${flow.intro.voice}` : ''}
            </span>
            <AudioStatusBadge entry={flow.intro} />
            <span className="w-16 shrink-0 text-right text-[10px] text-slate-500">
              {formatRelative(flow.intro.generatedAt) ?? ''}
            </span>
            <div className="flex shrink-0 items-center gap-1">
              <PlayBtn entry={flow.intro} playingUrl={playingUrl} onPlay={onPlay} />
              <IconBtn
                title="Regenerate intro audio"
                busy={busyKeys.has(`audio:${flow.id}:__summary__:force`)}
                onClick={() =>
                  void onRegen({ flowId: flow.id, stepIds: ['__summary__'], kind: 'audio', force: true })
                }
              >
                <RefreshCw className="h-3 w-3" />
              </IconBtn>
            </div>
          </div>

          {/* Step rows */}
          {flow.steps.map((step, idx) => (
            <div
              key={step.id}
              className="flex items-center gap-2 border-b border-slate-700/40 px-3 py-2 text-xs last:border-b-0"
            >
              <span className="w-6 shrink-0 text-center text-[10px] text-slate-500">{idx + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-slate-200">{step.title || step.id}</p>
                <p className="truncate text-[10px] text-slate-500">
                  {step.id}
                  {step.mascot_pose ? ` · pose ${step.mascot_pose}` : ''}
                  {step.mascot_position ? ` · ${step.mascot_position}` : ''}
                </p>
              </div>
              {(() => {
                const missing = (step.targets ?? []).filter((t) => t.status === 'missing');
                if (!missing.length) return null;
                return (
                  <span
                    className="shrink-0"
                    title={`Broken target${missing.length > 1 ? 's' : ''} — nothing in the codebase renders: ${missing
                      .map((t) => `${t.kind}:${t.id}`)
                      .join(', ')}. The card collapses to 'This section is not visible'.`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                  </span>
                );
              })()}
              <AudioStatusBadge entry={step.narration} />
              <TitleAudioDot entry={step.titleAudio} playingUrl={playingUrl} onPlay={onPlay} />
              <span className="w-16 shrink-0 text-right text-[10px] text-slate-500">
                {formatRelative(step.narration.generatedAt) ?? ''}
              </span>
              <div className="flex shrink-0 items-center gap-1">
                <PlayBtn entry={step.narration} playingUrl={playingUrl} onPlay={onPlay} />
                <IconBtn
                  title="Regenerate this step's audio"
                  busy={busyKeys.has(`audio:${flow.id}:${step.id}:force`)}
                  onClick={() =>
                    void onRegen({ flowId: flow.id, stepIds: [step.id], kind: 'audio', force: true })
                  }
                >
                  <RefreshCw className="h-3 w-3" />
                </IconBtn>
                <IconBtn title="Edit step in tour" onClick={() => onEditStep(flow.id, step.id)}>
                  <Pencil className="h-3 w-3" />
                </IconBtn>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VideoBadge({
  video,
  duration,
}: {
  video: TourMediaFlowStatus['video'];
  duration: string | null;
}) {
  return (
    <a
      href={video.videoUrl}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => {
        if (!video.videoUrl) e.preventDefault();
      }}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium',
        video.status === 'ready'
          ? 'border-indigo-500/30 bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25'
          : video.status === 'stale'
            ? 'border-amber-500/30 bg-amber-500/15 text-amber-300'
            : 'border-slate-600/50 bg-slate-700/30 text-slate-500',
      )}
      title={
        video.status === 'ready'
          ? `Video ready${video.generatedAt ? ` · ${formatRelative(video.generatedAt)}` : ''}`
          : video.status === 'stale'
            ? 'Video exists but the flow changed since it was recorded — it is not being served. Click to view the old recording; re-record it with the film button or "Record missing & stale".'
            : 'No video recorded for this flow'
      }
    >
      <Film className="h-3 w-3" />
      {video.status === 'ready' ? (duration ?? 'ready') : video.status}
    </a>
  );
}

function IconBtn({
  children,
  title,
  onClick,
  busy,
}: {
  children: React.ReactNode;
  title: string;
  onClick?: () => void;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={busy}
      className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-700/70 hover:text-white disabled:opacity-40"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : children}
    </button>
  );
}

function PlayBtn({
  entry,
  playingUrl,
  onPlay,
}: {
  entry: TourAudioStatusEntry;
  playingUrl: string | null;
  onPlay: (url?: string) => void;
}) {
  if (!entry.audioUrl) return null;
  const playing = playingUrl === entry.audioUrl;
  return (
    <button
      type="button"
      title={playing ? 'Stop preview' : 'Preview audio'}
      onClick={() => onPlay(entry.audioUrl)}
      className={cn(
        'rounded-md p-1.5 transition-colors',
        playing
          ? 'bg-indigo-500/30 text-indigo-200'
          : 'text-slate-400 hover:bg-slate-700/70 hover:text-white',
      )}
    >
      {playing ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
    </button>
  );
}

/**
 * Title-track audio — the short "<title>." read that plays ahead of each
 * step's narration. Shown as a small badge since it regenerates with the
 * step's own regen button.
 */
function TitleAudioDot({
  entry,
  playingUrl,
  onPlay,
}: {
  entry: TourAudioStatusEntry;
  playingUrl: string | null;
  onPlay: (url?: string) => void;
}) {
  if (entry.status === 'none') return null;
  return (
    <button
      type="button"
      title={`Title audio: ${AUDIO_STATUS_LABEL[entry.status]}${
        entry.durationSeconds ? ` · ${formatDuration(entry.durationSeconds)}` : ''
      }${entry.audioUrl ? ' — click to preview' : ''}`}
      onClick={() => entry.audioUrl && onPlay(entry.audioUrl)}
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-md border px-1 py-0.5 text-[9px] font-semibold uppercase',
        AUDIO_STATUS_CLS[entry.status],
        entry.audioUrl && playingUrl === entry.audioUrl && 'ring-1 ring-indigo-400',
      )}
    >
      {playingUrl === entry.audioUrl ? (
        <Square className="h-2.5 w-2.5" />
      ) : (
        <Clock className="h-2.5 w-2.5" />
      )}
      T
    </button>
  );
}
